import React, { useState, useEffect } from 'react';
import { CheckCircle, AlertCircle, X, Info } from 'lucide-react';

export interface ToastProps {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message?: string;
  duration?: number;
  onClose: (id: string) => void;
}

const Toast: React.FC<ToastProps> = ({ 
  id, 
  type, 
  title, 
  message, 
  duration = 3000, 
  onClose 
}) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // 入場動畫
    const showTimer = setTimeout(() => setIsVisible(true), 100);
    
    // 自動消失
    const hideTimer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(() => onClose(id), 300); // 等待退場動畫完成
    }, duration);

    return () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
    };
  }, [id, duration, onClose]);

  const typeStyles = {
    success: {
      bg: 'bg-green-50',
      border: 'border-green-400',
      icon: <CheckCircle className="w-5 h-5 text-green-600" />,
      titleColor: 'text-green-800',
      messageColor: 'text-green-700'
    },
    error: {
      bg: 'bg-red-50',
      border: 'border-red-400',
      icon: <AlertCircle className="w-5 h-5 text-red-600" />,
      titleColor: 'text-red-800',
      messageColor: 'text-red-700'
    },
    warning: {
      bg: 'bg-orange-50',
      border: 'border-orange-400',
      icon: <AlertCircle className="w-5 h-5 text-orange-600" />,
      titleColor: 'text-orange-800',
      messageColor: 'text-orange-700'
    },
    info: {
      bg: 'bg-blue-50',
      border: 'border-blue-400',
      icon: <Info className="w-5 h-5 text-blue-600" />,
      titleColor: 'text-blue-800',
      messageColor: 'text-blue-700'
    }
  };

  const style = typeStyles[type] || typeStyles.info;

  if (!style) {
    console.error('[Toast] Invalid toast type:', type);
    return null;
  }

  return (
    <div
      className={`
        max-w-sm w-full
        ${style.bg} ${style.border} border-3 rounded-2xl shadow-2xl
        transform transition-all duration-300 ease-out
        ${isVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}
      `}
    >
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0">
            {style.icon}
          </div>
          <div className="flex-1">
            <h3 className={`font-black text-sm ${style.titleColor}`}>
              {title}
            </h3>
            {message && (
              <p className={`text-sm font-semibold mt-1 ${style.messageColor}`}>
                {message}
              </p>
            )}
          </div>
          <button
            onClick={() => {
              setIsVisible(false);
              setTimeout(() => onClose(id), 300);
            }}
            className="flex-shrink-0 p-1 hover:bg-black hover:bg-opacity-10 rounded-full transition-colors"
          >
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Toast;
